import { createContext, useContext, type ReactNode } from 'react';

export interface SiteConfig {
  /** Absolute origin, e.g. https://cyber.byaft.com (no trailing slash). */
  siteUrl: string;
  /** Brand name used in the <title> template. */
  brandName: string;
}

const SiteConfigContext = createContext<SiteConfig>({
  siteUrl: 'https://byaft.com',
  brandName: 'BYAFT',
});

/**
 * Provides the per-app origin + brand name to the shared SEO hook, so one
 * `usePageMeta` works across byaft.com / tools.byaft.com / cyber.byaft.com
 * without a hardcoded domain. Wrap each app's tree in this.
 */
export function SiteConfigProvider({
  siteUrl,
  brandName = 'BYAFT',
  children,
}: {
  siteUrl: string;
  brandName?: string;
  children: ReactNode;
}) {
  return (
    <SiteConfigContext.Provider value={{ siteUrl, brandName }}>{children}</SiteConfigContext.Provider>
  );
}

export function useSiteConfig(): SiteConfig {
  return useContext(SiteConfigContext);
}

/**
 * Absolute, trailing-slash URL for a route path. Cloudflare Pages serves the
 * directory routes at the trailing-slash form (the non-slash URL 308-redirects),
 * so canonical / og:url / sitemap / JSON-LD all use this. Root stays "/".
 */
export function buildPageUrl(siteUrl: string, path: string): string {
  const clean = (path || '/').replace(/[?#].*$/, '');
  const slashed = clean === '/' ? '/' : `${clean.replace(/\/+$/, '')}/`;
  return siteUrl + slashed;
}
